import { Vertical } from "@/components/standard/BFLayout"
import {
  Pressable,
  ScrollView,
  View,
  KeyboardAvoidingView,
  Platform,
} from "react-native"
import { H2, H5, H6, BodyBold } from "@/components/standard/BFText"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import { Button } from "@/components/reusables/ui/button"
import Circle from "phosphor-react-native/src/icons/Circle"
import CheckCircle from "phosphor-react-native/src/icons/CheckCircle"
import { BFTextInput } from "@/components/standard/BFTextInput"
import { useUserProfileAttributionMutation } from "@/queries/user"

// Map display labels to backend values
const ATTRIBUTION_LABEL_TO_VALUE: Record<string, string> = {
  "Online ad": "Online Ad",
  Webinar: "Webinar",
  Referral: "Referral",
  Broker: "Broker",
  Event: "Event",
  Other: "Other",
}

const ATTRIBUTION_VALUES_WITH_INPUT = ["Other", "Referral"]

export interface SelectOption {
  value: string
  label: string
}

interface RadioOptionProps {
  option: string
  isSelected: boolean
  onPress: () => void
  testID?: string
}

export const RadioOption = ({
  option,
  isSelected,
  onPress,
  testID,
}: RadioOptionProps) => {
  return (
    <Pressable
      testID={testID}
      className={`py-3 px-4 rounded-lg shadow-sm border mb-3 ${
        isSelected
          ? "bg-off-white border-calm-blue"
          : "bg-white border-light-grey"
      } hover:bg-off-white active:bg-off-white`}
      onPress={onPress}
    >
      <View className="flex-row justify-between items-center">
        {isSelected ? (
          <CheckCircle color={BFColors.calmBlue} size={24} weight="fill" />
        ) : (
          <Circle color={BFColors.calmBlue} size={24} />
        )}
        <H6 color="primary" className="flex-1 pl-2">
          {option}
        </H6>
      </View>
    </Pressable>
  )
}

export interface AttributionPopupProps {
  visible: boolean
  onClose: () => void
}

export const AttributionPopup = ({
  visible,
  onClose,
}: AttributionPopupProps) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const [selectedValue, setSelectedValue] = useState<string | undefined>()
  const [extraText, setExtraText] = useState<string>("")
  const updateAttributionMutation = useUserProfileAttributionMutation()

  const shouldShowInput =
    selectedValue && ATTRIBUTION_VALUES_WITH_INPUT.includes(selectedValue)

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  useEffect(() => {
    if (!visible) {
      bottomSheetModalRef.current?.dismiss()
    } else {
      bottomSheetModalRef.current?.present()
    }
  }, [visible])

  const handleSubmit = async (value?: string) => {
    try {
      // Convert display label to backend value
      const backendValue = value
        ? ATTRIBUTION_LABEL_TO_VALUE[value] || value
        : ""
      await updateAttributionMutation.mutateAsync({
        attribution: backendValue,
        attributionExtra: extraText,
      })
    } catch (error) {
      console.error("Failed to update attribution:", error)
    }
    bottomSheetModalRef.current?.dismiss()
    onClose()
  }

  const handleSkip = async () => {
    await handleSubmit("")
  }

  let disabled = !selectedValue

  let extraLabel = ""
  if (selectedValue === "Other") {
    extraLabel = "Please tell us more"
    disabled = disabled || extraText.trim() === ""
  } else if (selectedValue === "Referral") {
    extraLabel = "Who referred you?"
    disabled = disabled || extraText.trim() === ""
  }

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={renderBackdrop}
      snapPoints={["90%"]}
      backgroundStyle={{ backgroundColor: "#FFFFFF" }}
      handleIndicatorStyle={{ backgroundColor: BFColors.lightGrey }}
      enableDynamicSizing={true}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
    >
      <BottomSheetView>
        <View className="flex-1 px-6">
          <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            className="flex-1"
            keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
          >
            <Vertical className="gap-4 mt-6 flex-1 pb-6">
              <Vertical className="gap-2">
                <H2 className="text-center" color="primary">
                  How did you hear about us?
                </H2>
              </Vertical>

              <ScrollView className="flex-1">
                <Vertical className="pt-4">
                  {Object.keys(ATTRIBUTION_LABEL_TO_VALUE).map(
                    (option, index) => (
                      <RadioOption
                        key={option}
                        option={option}
                        isSelected={selectedValue === option}
                        onPress={() => {
                          setSelectedValue(option)
                        }}
                        testID={`attribution-option-${index}`}
                      />
                    )
                  )}
                </Vertical>
              </ScrollView>
              <Vertical className="mt-5 gap-3">
                {shouldShowInput && (
                  <Vertical className="mb-4 gap-3">
                    <H5 className="text-foreground">{extraLabel}</H5>
                    <BFTextInput
                      variant="form"
                      value={extraText}
                      onChangeText={setExtraText}
                      testID="attribution-detail-input"
                    />
                  </Vertical>
                )}
                <Button
                  testID="attribution-submit"
                  className="bg-calm-blue rounded-full py-3"
                  onPress={() => {
                    void handleSubmit(selectedValue)
                  }}
                  disabled={disabled}
                >
                  <BodyBold className="text-teal">Submit</BodyBold>
                </Button>
                <Button
                  testID="attribution-skip"
                  variant="ghost"
                  className="rounded-full py-3 border-midnight-blue"
                  onPress={() => {
                    void handleSkip()
                  }}
                >
                  <BodyBold className="text-midnight-blue">Skip</BodyBold>
                </Button>
              </Vertical>
            </Vertical>
          </KeyboardAvoidingView>
        </View>
      </BottomSheetView>
    </BottomSheetModal>
  )
}
